import React, { useEffect, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { Modal } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import COLORS from "../../../../assets/Colors";
import theme from "../../../../assets/theme";

// The shell of the EPE (JustCue) dialogs, in the look of ManagementModal.js. MUI's Modal keeps the focus inside
// and gives it back to the opener on close. On open the focus moves to `initialFocusRef` (else to the dialog
// itself), Tab and Shift+Tab wrap around, and Esc or a press that both starts and ends on the backdrop closes
// the dialog, except while `busy` (a request is out and its answer must not be lost).

const EASE_OUT_EXPO = "cubic-bezier(0.16, 1, 0.3, 1)";
const EASE_SPRING = "cubic-bezier(0.34, 1.56, 0.64, 1)";

const motionSafe = (animationCss) => css`
  ${animationCss}
  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    transition: none !important;
  }
`;

const scaleFadeIn = keyframes`
  from { opacity: 0; transform: scale(0.96) translateY(10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const spin = keyframes`from { transform: rotate(0deg) } to { transform: rotate(360deg) }`;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const focusables = (root) =>
  Array.from(root.querySelectorAll(FOCUSABLE)).filter(
    (el) => el.getClientRects().length > 0,
  );

export default function EpeDialog({ onClose, busy = false, ...panelProps }) {
  // The press started on the backdrop itself, and was not let go inside the dialog.
  const pressOnBackdrop = useRef(false);

  const close = () => {
    if (!busy) onClose();
  };

  return (
    <Modal open onClose={close}>
      <ModalOverlay
        onMouseDown={(e) => {
          pressOnBackdrop.current = e.target === e.currentTarget;
        }}
        onMouseUp={(e) => {
          if (e.target !== e.currentTarget) pressOnBackdrop.current = false;
        }}
        onClick={(e) => {
          // A press from the dialog to the backdrop (or back) clicks the backdrop too, as their common ancestor.
          const onBackdrop =
            pressOnBackdrop.current && e.target === e.currentTarget;
          pressOnBackdrop.current = false;
          if (onBackdrop) close();
        }}
      >
        <DialogPanel {...panelProps} busy={busy} close={close} />
      </ModalOverlay>
    </Modal>
  );
}

function DialogPanel({
  id,
  title,
  description,
  busy,
  close,
  initialFocusRef,
  panelRef: panelRefProp,
  maxWidth = 600,
  footer,
  children,
}) {
  const ownPanelRef = useRef(null);
  const panelRef = panelRefProp || ownPanelRef;
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;

  // MUI's Portal mounts this one render after the Modal, so this runs once the dialog is really in the page, and
  // before the Modal's focus trap would focus the backdrop.
  useEffect(() => {
    const el = initialFocusRef?.current || panelRef.current;
    if (el) el.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = (event) => {
    if (event.key !== "Tab" || !panelRef.current) return;
    const items = focusables(panelRef.current);
    if (!items.length) {
      event.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const activeEl = document.activeElement;
    if (event.shiftKey && (activeEl === first || activeEl === panelRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && activeEl === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <ModalWrapper
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      aria-busy={busy || undefined}
      tabIndex={-1}
      $maxWidth={maxWidth}
      onKeyDown={handleKeyDown}
    >
      <ModalTopBar>
        <TopBarText>
          <TopBarTitle id={titleId}>{title}</TopBarTitle>
          {description && (
            <TopBarSubtitle id={descriptionId}>{description}</TopBarSubtitle>
          )}
        </TopBarText>
        <CloseButton
          type="button"
          onClick={close}
          disabled={busy}
          aria-label="Close"
        >
          <CloseIcon style={{ fontSize: 18 }} />
        </CloseButton>
      </ModalTopBar>

      <ModalBody>{children}</ModalBody>

      {footer && <ModalFooter>{footer}</ModalFooter>}
    </ModalWrapper>
  );
}

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(26, 35, 56, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1300;

  &:focus {
    outline: none;
  }
`;

const ModalWrapper = styled.div`
  background: white;
  border-radius: 22px;
  box-shadow: 0 24px 60px -18px rgba(15, 23, 42, 0.45);
  width: 100%;
  max-width: ${(props) => props.$maxWidth}px;
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(148, 163, 184, 0.35);
  ${motionSafe(css`
    animation: ${scaleFadeIn} 0.32s ${EASE_OUT_EXPO} both;
  `)}

  &:focus {
    outline: none;
  }
`;

const ModalTopBar = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  flex-shrink: 0;
`;

const TopBarText = styled.div`
  min-width: 0;
`;

const TopBarTitle = styled.h2`
  font-size: 18px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
  margin: 0;
`;

const TopBarSubtitle = styled.p`
  font-size: 13px;
  line-height: 1.55;
  color: #64748b;
  margin: 4px 0 0;
`;

const CloseButton = styled.button`
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  background: #f1f5f9;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 0.2s ${EASE_OUT_EXPO},
    transform 0.2s ${EASE_SPRING};

  &:hover:not(:disabled) {
    background: #e2e8f0;
    transform: rotate(90deg);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ModalBody = styled.div`
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  min-height: 0;
  overflow: auto;
  background:
    radial-gradient(
      circle at top right,
      rgba(0, 129, 138, 0.05),
      transparent 35%
    ),
    #ffffff;

  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 8px;
  }
`;

const ModalFooter = styled.div`
  padding: 14px 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  background: #f8fafc;
  flex-shrink: 0;
`;

export const DialogPrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 120px;
  padding: 10px 20px;
  border: 1px solid ${COLORS.BTN_GREEN};
  border-radius: 10px;
  background: ${COLORS.BTN_GREEN};
  color: #ffffff;
  font-size: 13.5px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  cursor: pointer;
  transition:
    background 0.2s ${EASE_OUT_EXPO},
    border-color 0.2s ${EASE_OUT_EXPO},
    transform 0.2s ${EASE_SPRING};

  &:hover:not(:disabled):not([aria-disabled="true"]) {
    background: #006970;
    border-color: #006970;
    transform: translateY(-1px);
  }

  &:disabled,
  &[aria-disabled="true"] {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const DialogSecondaryButton = styled(DialogPrimaryButton)`
  background: #ffffff;
  color: ${COLORS.BTN_GREEN};

  &:hover:not(:disabled):not([aria-disabled="true"]) {
    background: #e6f2f3;
    border-color: ${COLORS.BTN_GREEN};
  }
`;

// A refusal or warning, with the amber accent of the case page.
export const DialogNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  border-left: 4px solid #f6b436;
  background: #f8fafc;
  color: ${COLORS.COLOR_DARK};
  font-size: 13px;
  line-height: 1.55;
  font-family: ${theme.fonts.primaryFontRegular};
  word-break: break-word;
`;

export const DialogSpinner = styled.span`
  display: inline-block;
  flex-shrink: 0;
  width: ${(props) => props.$size || 14}px;
  height: ${(props) => props.$size || 14}px;
  border-radius: 50%;
  border: 2px solid ${(props) => props.$track || "rgba(255, 255, 255, 0.35)"};
  border-top-color: ${(props) => props.$color || "#ffffff"};
  animation: ${spin} 0.7s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2s;
  }
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
